import React, { useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import {
	Navigate,
	Route,
	Routes,
	useNavigate,
	useParams,
} from 'react-router-dom';
import { AuthData } from '../../../context/auth';
import {
	RenewalContext,
	RenewalData,
	withRenewalContext,
} from '../../../context/RenewalProvider';
import AuxDataView from '../../auxData/AuxDataView';
import { StyledContentPane } from '../../common';
import DisabledOverlay from '../../DisabledOverlay';
import { useFastForm } from '../../fastForm';
import { getDefaultPolicyValues } from '../../fastForm/formBuilders';
import LargeCenterSpinner from '../../LargeCenterSpinner';
import LeftSidebarLayout from '../../layouts/LeftSidebarLayout';
import RenewalLayout from '../../layouts/RenewalLayout';
import ProtectedRoute from '../../ProtectedRoute';
import DocumentsView from '../Renewals/DocumentsView';
import Exposures from '../Renewals/Exposures';
import InvoicesView from '../Renewals/InvoicesView';
import InvoiceView from '../Renewals/InvoiceView';
import PolicyDetails from '../Renewals/PolicyDetails';
import PricePreview from '../Renewals/PricePreview';
import ReviewChanges from '../Renewals/ReviewChanges';
import RenewalSidebar from '../Sidebar/RenewalSidebar';
import RenewalExposureRoutes from './RenewalExposureRoutes';

interface Props {
	renewalData: RenewalData;
	authData: AuthData;
}

// Route: '.../policy/:policyId/renewals/:renewalId'
const RenewalRoutes = (props: Props) => {
	const { renewalData, authData } = props;
	const { setRenewalUpdateObj, policy, renewalUpdateObj, fields } = renewalData;
	const params = useParams();
	const navigate = useNavigate();
	const { policyId, renewalId } = params;
	const [loading, setLoading] = useState<boolean>(false);
	const methods = useFastForm(fields, {});
	const { reset } = methods;

	useEffect(() => {
		const populateForm = () => {
			if (fields) {
				const defaultRenewalValues = getDefaultPolicyValues(fields, authData);
				reset(defaultRenewalValues);
			}
		};
		populateForm();
	}, [fields, reset, authData]);

	useEffect(() => {
		if (renewalId && !loading) {
			const load = async () => {
				setLoading(true);
				await renewalData.loadData(policyId, renewalId);
				setLoading(false);
			};

			load();
		}

		return () => {
			setRenewalUpdateObj(null);
		};
	}, [policyId, renewalId]);

	const renderMain = () => {
		if (loading || !policy || !renewalUpdateObj) {
			return <LargeCenterSpinner />;
		}

		const defaultUrl =
			renewalUpdateObj.state === 'application'
				? `policyDetails`
				: `price-breakdown`;

		return (
			<StyledContentPane>
				<RenewalLayout>
					<Routes>
						<Route path='policyDetails' element={<PolicyDetails />} />
						<Route
							path='exposures/:exposureId/*'
							element={<RenewalExposureRoutes />}
						/>
						<Route path='exposures' element={<Exposures />} />
						<Route path='review' element={<ReviewChanges />} />
						<Route path='price-breakdown' element={<PricePreview />} />
						<Route
							path='invoices/:invoiceId'
							element={
								<ProtectedRoute permission='invoice.fetch'>
									<InvoiceView />
								</ProtectedRoute>
							}
						/>
						<Route
							path='invoices'
							element={
								<ProtectedRoute permission='invoice.fetch'>
									<InvoicesView />
								</ProtectedRoute>
							}
						/>
						<Route path='documents' element={<DocumentsView />} />
						<Route
							path='auxData'
							element={
								<ProtectedRoute permission='auxData.fetch'>
									<AuxDataView
										navigate={navigate}
										entityLocator={renewalId}
										entityType={'renewal'}
									/>
								</ProtectedRoute>
							}
						/>
						<Route path='/' element={<Navigate replace to={defaultUrl} />} />
					</Routes>
				</RenewalLayout>
			</StyledContentPane>
		);
	};

	const renderSidebar = () => {
		if (loading || !policy || !renewalUpdateObj) {
			return <DisabledOverlay />;
		}

		return <RenewalSidebar />;
	};

	return (
		<FormProvider {...methods}>
			<RenewalContext.Provider value={renewalData}>
				<LeftSidebarLayout
					leftSidebarContent={renderSidebar()}
					mainContent={renderMain()}
				/>
			</RenewalContext.Provider>
		</FormProvider>
	);
};

export default withRenewalContext<Props>(RenewalRoutes);
